<script setup lang="ts">
import type {
  RemoteDatatableColumn,
  RemoteDatatableFilter,
} from '~/components/RemoteDatatable.vue';
import { CaretDownFilled, SettingOutlined } from '@ant-design/icons-vue';
import { reactive, ref, toRaw, watch } from 'vue';
import estadoSelect from '~/graphql/estadoSelect.graphql';
import tipoCompromissoSelect from '~/graphql/tipoCompromissoSelect.graphql';
import tipoFluxoSelect from '~/graphql/tipoFluxoSelect.graphql';
import usuarioSelect from '~/graphql/usuarioSelect.graphql';
import PERFIL_ACESSO from '~/perfilAcesso';
import { useAuthStore } from '~/stores/auth';
import { useDrawerStore } from '~/stores/drawer';
import { isNilOrEmpty } from '~/utils';

interface Props {
  disabledFilters?: string[];
  showStatusSearchBar?: boolean;
  showDatesFilter?: boolean;
  showFilter?: boolean;
  showReloadButton?: boolean;
  showTextSearch?: boolean;
  columns?: RemoteDatatableColumn[];
  enableRowSelection?: boolean;
  totalPerPage?: number;
  reloadInterval?: number;
  showExportButton?: boolean;

  whereTipoEq?: number;
  whereTipoIn?: number[];
  whereCodigoIn?: string[];
  whereResponsavelCodigoIn?: string[];
  whereFluxoTipoIn?: number[];
  whereFluxoCodigoIn?: string[];
  whereFluxoPastaCodigoIn?: string[];
  whereFluxoPastaNomeIn?: string[];
  whereFluxoPastaGcpjIn?: string[];
  whereFluxoPastaNumeroCnjIn?: string[];
  whereFluxoPastaResponsavelProcessualCodigoIn?: string[];
  whereFluxoPastaEstadoCodigoIn?: string[];

  orderDataInicio?: 'asc' | 'desc';
  orderDataVencimento?: 'asc' | 'desc';
  orderDataConclusao?: 'asc' | 'desc';
}

const props = withDefaults(defineProps<Props>(), {
  disabledFilters: () => [],
  showStatusSearchBar: true,
  showFilter: true,
  showDatesFilter: true,
  showReloadButton: true,
  enableRowSelection: false,
  showTextSearch: false,
  totalPerPage: 10,
  showExportButton: true,
});

const emit = defineEmits(['load', 'reload']);

const authStore = useAuthStore();
const drawerStore = useDrawerStore();

const remoteVariables = ref<any>({});
const tableRef = ref<any>(null);
const state = reactive<any>({
  status: null,
  dataInicioRange: null,
  dataVencimentoRange: null,
});

const filters = [] as RemoteDatatableFilter[];

if (props.showFilter && !props.disabledFilters.find((x) => x === 'codigo')) {
  filters.push({
    key: 'codigo',
    label: 'Códigos de compromissos',
    path: 'whereCodigoIn',
    type: 'text-multiple',
    queryKey: 'c',
    componentProps: {
      placeholder: 'Digite um ou mais códigos',
    },
  });
}

if (props.showFilter && !props.disabledFilters.find((x) => x === 'tipo')) {
  filters.push({
    key: 'tipo',
    label: 'Tipos de compromisso',
    path: 'whereTipoIn',
    type: 'gql-select',
    queryKey: 'tc',
    componentProps: {
      placeholder: 'Selecione um ou mais tipos de compromisso',
      query: tipoCompromissoSelect,
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'dataInicio')
) {
  filters.push({
    key: 'dataInicio',
    label: 'Data de inicio',
    path: 'whereDataInicio',
    type: 'date',
    queryKey: 'di',
    componentProps: {
      format: 'DD/MM/YYYY',
      placeholder: ['Inicial', 'Final'],
      style: 'width: 100%',
      valueFormat: 'DD/MM/YYYY',
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'dataConclusao')
) {
  filters.push({
    key: 'dataConclusao',
    label: 'Data de conclusão',
    path: 'whereDataConclusao',
    type: 'date',
    queryKey: 'dc',
    componentProps: {
      format: 'DD/MM/YYYY',
      placeholder: ['Inicial', 'Final'],
      style: 'width: 100%',
      valueFormat: 'DD/MM/YYYY',
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'fluxoDataInicio')
) {
  filters.push({
    key: 'fluxoDataInicio',
    label: 'Fluxo / Data de início',
    path: 'whereFluxoDataInicio',
    type: 'date',
    queryKey: 'fdi',
    componentProps: {
      format: 'DD/MM/YYYY',
      placeholder: ['Inicial', 'Final'],
      style: 'width: 100%',
      valueFormat: 'DD/MM/YYYY',
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'fluxoDataConclusao')
) {
  filters.push({
    key: 'fluxoDataConclusao',
    label: 'Fluxo / Data de conclusão',
    path: 'whereFluxoDataConclusao',
    type: 'date',
    queryKey: 'fdc',
    componentProps: {
      format: 'DD/MM/YYYY',
      placeholder: ['Inicial', 'Final'],
      style: 'width: 100%',
      valueFormat: 'DD/MM/YYYY',
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'responsavel')
) {
  filters.push({
    key: 'responsavel',
    label: 'Responsável pelo compromisso',
    path: 'whereResponsavelCodigoIn',
    type: 'gql-select',
    queryKey: 'r',
    componentProps: {
      placeholder: 'Selecione um ou mais responsáveis',
      query: usuarioSelect,
    },
  });
}

if (props.showFilter && !props.disabledFilters.find((x) => x === 'tipoFluxo')) {
  filters.push({
    key: 'tipoFluxo',
    label: 'Tipos de fluxo',
    path: 'whereFluxoTipoIn',
    type: 'gql-select',
    queryKey: 'tf',
    componentProps: {
      placeholder: 'Selecione um ou mais tipos de fluxo',
      query: tipoFluxoSelect,
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'fluxoCodigo')
) {
  filters.push({
    key: 'fluxoCodigo',
    label: 'Códigos de fluxos',
    path: 'whereFluxoCodigoIn',
    type: 'text-multiple',
    queryKey: 'fc',
    componentProps: {
      placeholder: 'Digite um ou mais códigos de fluxos',
    },
  });
}

if (props.showFilter && !props.disabledFilters.find((x) => x === 'pastaNome')) {
  filters.push({
    key: 'pastaNome',
    label: 'Nomes de pastas',
    path: 'whereFluxoPastaNomeIn',
    type: 'text-multiple',
    queryKey: 'pn',
    componentProps: {
      placeholder: 'Digite um ou mais nomes',
    },
  });
}

if (props.showFilter && !props.disabledFilters.find((x) => x === 'pastaGcpj')) {
  filters.push({
    key: 'pastaGcpj',
    label: 'GCPJs de pastas',
    path: 'whereFluxoPastaGcpjIn',
    type: 'text-multiple',
    queryKey: 'pg',
    componentProps: {
      placeholder: 'Digite um ou mais GCPJs',
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'pastaNumeroCnj')
) {
  filters.push({
    key: 'pastaNumeroCnj',
    label: 'Número CNJ de pastas',
    path: 'whereFluxoPastaNumeroCnjIn',
    type: 'text-multiple',
    queryKey: 'pncnj',
    componentProps: {
      placeholder: 'Digite um ou mais números CNJ',
    },
  });
}

if (
  props.showFilter &&
  !props.disabledFilters.find((x) => x === 'responsavelProcessual')
) {
  filters.push({
    key: 'responsavelProcessual',
    label: 'Responsável processual da pasta',
    path: 'whereFluxoPastaResponsavelProcessualCodigoIn',
    type: 'gql-select',
    queryKey: 'rp',
    componentProps: {
      placeholder: 'Selecione um ou mais responsáveis',
      query: usuarioSelect,
    },
  });
}

if (props.showFilter && !props.disabledFilters.find((x) => x === 'estado')) {
  filters.push({
    key: 'estado',
    label: 'Estado',
    path: 'whereFluxoPastaEstadoCodigoIn',
    type: 'gql-select',
    queryKey: 'e',
    componentProps: {
      placeholder: 'Selecione um ou mais estados',
      query: estadoSelect,
    },
  });
}

const colunas =
  props.columns ??
  ([
    {
      key: 'compromisso',
      title: 'COMPROMISSO',
      width: 120,
    },
    {
      key: 'status',
      dataIndex: 'statusText',
      title: 'STATUS',
      width: 120,
    },
    {
      key: 'responsavel',
      title: 'RESPONSÁVEL',
      width: 120,
    },
    {
      key: 'pasta',
      title: 'PASTA',
      width: 120,
    },
    {
      key: 'contrario',
      title: 'CONTRÁRIO',
      width: 120,
    },
    {
      dataIndex: 'dataInicioText',
      key: 'dataInicio',
      sortBy: ['dataInicio'],
      sorter: true,
      title: 'SOLICITADO EM',
      width: 120,
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'dataVencimentoText',
      key: 'dataVencimento',
      sortBy: ['dataVencimento'],
      sorter: true,
      title: 'VENCIMENTO EM',
      width: 120,
    },
    {
      dataIndex: 'dataConclusaoText',
      key: 'dataConclusao',
      sortBy: ['dataConclusao'],
      sorter: true,
      title: 'CONCLUÍDO EM',
      width: 120,
    },
  ] as RemoteDatatableColumn[]);

function abrirCompromisso(compromissoCodigo: string) {
  drawerStore.push2({
    componentName: 'CompromissoView',
    onClose: async () => {
      if (tableRef.value) {
        await tableRef.value?.reloadAsync();
      }
    },
    params: { compromisso: { codigo: compromissoCodigo } },
  });
}

const compromissosSelecionados = ref<any[]>([]);

function onSelectChange(keys: any[]) {
  compromissosSelecionados.value = [...keys];
}

function acoesCompromissosSelecionadosOnClick({ key }: any) {
  if (key === 'delegar') {
    drawerStore.push2({
      componentName: 'DelegarCompromissoForm',
      onClose: async () => {
        await tableRef.value?.reloadAsync();
      },
      params: {
        compromissosCodigos: compromissosSelecionados.value,
      },
      width: 600,
    });
  }
  if (key === 'alterar-vencimento') {
    drawerStore.push2({
      componentName: 'AlterarVencimentoCompromissoForm',
      onClose: async () => {
        await tableRef.value?.reloadAsync();
      },
      params: {
        compromissosCodigos: compromissosSelecionados.value,
      },
      width: 600,
    });
  }
}

function buildVariables() {
  const vars: any = {};

  if (state.status) vars.status = state.status;

  if (state.dataInicioRange?.length > 0) {
    vars.whereDataInicioGte = `${state.dataInicioRange[0]} 00:00:00`;
    vars.whereDataInicioLte = `${state.dataInicioRange[1]} 23:59:59`;
  }
  if (state.dataVencimentoRange?.length > 0) {
    vars.whereDataVencimentoGte = `${state.dataVencimentoRange[0]} 00:00:00`;
    vars.whereDataVencimentoLte = `${state.dataVencimentoRange[1]} 23:59:59`;
  }

  if (props.whereTipoEq != null) vars.whereTipoEq = props.whereTipoEq;
  if (props.whereTipoIn?.length) vars.whereTipoIn = props.whereTipoIn;
  if (props.whereCodigoIn?.length) vars.whereCodigoIn = props.whereCodigoIn;
  if (props.whereResponsavelCodigoIn?.length)
    vars.whereResponsavelCodigoIn = props.whereResponsavelCodigoIn;
  if (props.whereFluxoTipoIn?.length)
    vars.whereFluxoTipoIn = props.whereFluxoTipoIn;
  if (props.whereFluxoCodigoIn?.length)
    vars.whereFluxoCodigoIn = props.whereFluxoCodigoIn;
  if (props.whereFluxoPastaCodigoIn?.length)
    vars.whereFluxoPastaCodigoIn = props.whereFluxoPastaCodigoIn;
  if (props.whereFluxoPastaNomeIn?.length)
    vars.whereFluxoPastaNomeIn = props.whereFluxoPastaNomeIn;
  if (props.whereFluxoPastaGcpjIn?.length)
    vars.whereFluxoPastaGcpjIn = props.whereFluxoPastaGcpjIn;
  if (props.whereFluxoPastaNumeroCnjIn?.length)
    vars.whereFluxoPastaNumeroCnjIn = props.whereFluxoPastaNumeroCnjIn;
  if (props.whereFluxoPastaResponsavelProcessualCodigoIn?.length)
    vars.whereFluxoPastaResponsavelProcessualCodigoIn =
      props.whereFluxoPastaResponsavelProcessualCodigoIn;
  if (props.whereFluxoPastaEstadoCodigoIn?.length)
    vars.whereFluxoPastaEstadoCodigoIn = props.whereFluxoPastaEstadoCodigoIn;

  if (props.orderDataInicio) vars.orderDataInicio = props.orderDataInicio;
  if (props.orderDataVencimento)
    vars.orderDataVencimento = props.orderDataVencimento;
  if (props.orderDataConclusao)
    vars.orderDataConclusao = props.orderDataConclusao;
  if (
    !props.orderDataInicio &&
    !props.orderDataVencimento &&
    !props.orderDataConclusao
  ) {
    vars.orderDataInicio = 'desc';
  }

  remoteVariables.value = { ...vars };
}

watch(state, () => {
  buildVariables();
});

watch(
  () => [
    props.whereTipoEq,
    props.whereTipoIn,
    props.whereCodigoIn,
    props.whereResponsavelCodigoIn,
    props.whereFluxoTipoIn,
    props.whereFluxoCodigoIn,
    props.whereFluxoPastaCodigoIn,
    props.whereFluxoPastaNomeIn,
    props.whereFluxoPastaGcpjIn,
    props.whereFluxoPastaNumeroCnjIn,
    props.whereFluxoPastaResponsavelProcessualCodigoIn,
    props.whereFluxoPastaEstadoCodigoIn,
    props.orderDataInicio,
    props.orderDataVencimento,
    props.orderDataConclusao,
  ],
  () => {
    buildVariables();
  },
);

buildVariables();

defineExpose({
  reload: async () => {
    await tableRef.value?.reloadAsync();
  },
  getSelectedRowsIds: () => {
    return toRaw(compromissosSelecionados.value);
  },
});
</script>

<template>
  <a-row v-if="props.showStatusSearchBar" style="margin-bottom: 16px">
    <a-col :span="24" style="text-align: right">
      <a-space style="text-align: left" size="middle">
        <a-radio-group v-model:value="state.status" size="large">
          <a-radio-button :value="null"> Todos </a-radio-button>
          <a-radio-button :value="1"> Atrasado </a-radio-button>
          <a-radio-button :value="2"> Vencimento hoje </a-radio-button>
          <a-radio-button :value="3">
            Vencimento nos próximos dias
          </a-radio-button>
          <a-radio-button :value="7"> Em andamento </a-radio-button>
          <a-radio-button :value="4"> Devolvido </a-radio-button>
          <a-radio-button :value="5"> Concluído </a-radio-button>
          <a-radio-button :value="6"> Cancelado </a-radio-button>
        </a-radio-group>
      </a-space>
    </a-col>
  </a-row>
  <RemoteDatatable
    ref="tableRef"
    url="/compromissos/datatable"
    :columns="colunas"
    :variables="remoteVariables"
    :filters="filters"
    :keep-history="false"
    :show-reload-button="props.showReloadButton"
    :text-search="
      props.showTextSearch
        ? {
            placeholder:
              'Busca rápida de compromissos por atributos das pastas (nome, GCPJ, número CNJ, número antigo, status, tipo da ação, cliente principal, contrário principal, responsável processual ou negocial, nome da agência, regional e jurisdição)',
          }
        : null
    "
    row-key="codigo"
    :row-selection="
      props.enableRowSelection
        ? {
            selectedRowKeys: compromissosSelecionados,
            onChange: onSelectChange,
          }
        : null
    "
    :total-per-page="props.totalPerPage"
    :reload-interval="props.reloadInterval"
    @load="(dataSource: any) => emit('load', dataSource)"
    @reload="(dataSource: any) => emit('reload', dataSource)"
  >
    <template v-if="props.showDatesFilter" #leftTopActions>
      <a-col flex="350px">
        <a-range-picker
          v-model:value="state.dataInicioRange"
          :placeholder="['Data solicitação inicial', 'Data solicitiação final']"
          format="DD/MM/YYYY"
          value-format="DD/MM/YYYY"
          size="large"
        />
      </a-col>
      <a-col flex="350px">
        <a-range-picker
          v-model:value="state.dataVencimentoRange"
          :placeholder="['Data vencimento inicial', 'Data vencimento final']"
          format="DD/MM/YYYY"
          value-format="DD/MM/YYYY"
          size="large"
        />
      </a-col>
      <a-col
        v-if="
          compromissosSelecionados.length > 0 &&
          (authStore.temPerfilAcesso(
            PERFIL_ACESSO.AutomatizacaoDelegarEmLote,
          ) ||
            authStore.temPerfilAcesso(
              PERFIL_ACESSO.AutomatizacaoAlterarVencimentoEmLote,
            ))
        "
        flex="100px"
      >
        <a-dropdown>
          <template #overlay>
            <a-menu @click="acoesCompromissosSelecionadosOnClick">
              <a-menu-item
                v-if="
                  authStore.temPerfilAcesso(
                    PERFIL_ACESSO.AutomatizacaoDelegarEmLote,
                  )
                "
                key="delegar"
              >
                Delegar
              </a-menu-item>
              <a-menu-item
                v-if="
                  authStore.temPerfilAcesso(
                    PERFIL_ACESSO.AutomatizacaoAlterarVencimentoEmLote,
                  )
                "
                key="alterar-vencimento"
              >
                Alterar vencimento
              </a-menu-item>
            </a-menu>
          </template>
          <a-button block size="large">
            <template #icon>
              <SettingOutlined />
            </template>
            Ações <CaretDownFilled />
          </a-button>
        </a-dropdown>
      </a-col>
    </template>

    <template #compromissoColumn="{ record, column }">
      <slot name="compromissoColumn" :column="column" :record="record">
        <a
          href="#"
          style="text-transform: uppercase"
          @click.prevent="abrirCompromisso(record.codigo)"
          >{{ record.tipoText }}</a
        >
      </slot>
    </template>
    <template #responsavelColumn="{ record }">
      <span style="text-transform: uppercase"
        >{{ record.responsavel?.nome }}
      </span>
    </template>
    <template #pastaColumn="{ record }">
      <router-link
        v-if="!isNilOrEmpty(record.fluxo.pasta)"
        :to="`/pasta/${record.fluxo.pasta.codigo}`"
      >
        {{ record.fluxo.pasta.nome }}
      </router-link>
    </template>
    <template #contrarioColumn="{ record }">
      <span v-if="!isNilOrEmpty(record.fluxo?.pasta?.contrarioPrincipal)">{{
        record.fluxo.pasta.contrarioPrincipal?.nome
      }}</span>
    </template>
  </RemoteDatatable>
</template>
